import {updateCheckboxSettingState} from "../../settings/settings_common";
import {maybeShowGCalHelp} from "../../settings/gcal_help_modal";

let tokenClient = null;
let accessToken = null;
const SELECTED_CALENDARS_STORAGE_KEY = "zima.gcal.selectedCalendarIds.v1";
const CALENDAR_START_HOUR = 6;
const ACTIVE_HOURS = 14;
const MIN_EVENT_HEIGHT_PX = 44;
let calendars = [];
let eventColors = {};

function initializeDayColumns() {
    let calendarHeight = document.getElementById("calendar").clientHeight;
    // initialize event day column elements
    let d1 = document.getElementById("row_start");
    for (let i = 0; i < 3; i++) {
        if (d1) {
            let day_name = document.createElement("div");
            day_name.classList.add("header");
            day_name.textContent = document.querySelectorAll(".weather-card__date")[i].textContent;
            d1.append(day_name);
            d1.classList.add("calendar__eventday");
            d1.style.height = `${calendarHeight}px`;
            d1 = d1.nextElementSibling;
        }
    }
}

function clearCalendarEventsView() {
    document.querySelectorAll(`#calendar > div .event-dots`).forEach(el => {
        el.innerHTML = "";
    });
    // clean event day column elements
    let d1 = document.getElementById("row_start");
    for (let i = 0; i < 3; i++) {
        if (d1) {
            d1.className = "";
            d1.innerHTML = "";
            d1 = d1.nextElementSibling;
        }
    }
}

function getCalendarSelector() {
    return document.getElementById("gcal_calendar_selector");
}

function getCalendarSelectorList() {
    return document.getElementById("gcal_calendar_selector_list");
}

function getCalendarSelectorStatus() {
    return document.getElementById("gcal_calendar_selector_status");
}

function showCalendarSelector() {
    const selector = getCalendarSelector();
    if (selector) {
        selector.classList.remove("hidden");
    }
}

function hideCalendarSelector() {
    const selector = getCalendarSelector();
    if (selector) {
        selector.classList.add("hidden");
    }
}

function setCalendarSelectorStatus(message = "") {
    const status = getCalendarSelectorStatus();
    if (!status) {
        return;
    }
    status.textContent = message;
    status.classList.toggle("hidden", message === "");
}

function getPrimaryCalendarId(readableCalendars) {
    const primaryCalendar = readableCalendars.find(calendar => calendar.primary);
    return primaryCalendar ? primaryCalendar.id : readableCalendars[0]?.id;
}

function readStoredSelectedCalendarIds(readableCalendars) {
    const readableCalendarIds = new Set(readableCalendars.map(calendar => calendar.id));
    try {
        const storedValue = localStorage.getItem(SELECTED_CALENDARS_STORAGE_KEY);
        if (storedValue !== null) {
            return JSON.parse(storedValue).filter(calendarId => readableCalendarIds.has(calendarId));
        }
    } catch (error) {
        console.warn("Failed to read Google Calendar selection", error);
    }

    const defaultCalendarId = getPrimaryCalendarId(readableCalendars);
    return defaultCalendarId ? [defaultCalendarId] : [];
}

function writeSelectedCalendarIds(selectedCalendarIds) {
    localStorage.setItem(SELECTED_CALENDARS_STORAGE_KEY, JSON.stringify(selectedCalendarIds));
}

function getSelectedCalendarIds() {
    const list = getCalendarSelectorList();
    if (!list) {
        const primaryCalendarId = getPrimaryCalendarId(calendars);
        return primaryCalendarId ? [primaryCalendarId] : [];
    }
    return Array.from(list.querySelectorAll("input[name='gcal_calendar']:checked")).map(input => input.value);
}

function getCalendarColor(calendarId) {
    const calendar = calendars.find(calendar => calendar.id === calendarId);
    return calendar?.backgroundColor || "#cccccc";
}

function getEventStartTime(event) {
    return new Date(event.start.dateTime || event.start.date).getTime();
}

function getEventEndTime(event) {
    return new Date(event.end.dateTime || event.end.date).getTime();
}

function getEventColor(event) {
    if (event.colorId && eventColors[event.colorId]) {
        return eventColors[event.colorId].background;
    }
    return event.calendarColor || getCalendarColor(event.calendarId);
}

function getVisualEventBounds(event, container) {
    const startTime = new Date(event.start.dateTime);
    const endTime = new Date(event.end.dateTime);
    const durationHours = Math.max((endTime - startTime) / (1000 * 60 * 60), 0);
    const minHeightPercent = container.clientHeight > 0 ? MIN_EVENT_HEIGHT_PX / container.clientHeight * 100 : 0;
    const heightPercent = Math.max(durationHours / ACTIVE_HOURS * 100, minHeightPercent);
    const offsetHours = Math.max(startTime.getHours() + startTime.getMinutes() / 60 - CALENDAR_START_HOUR, 0);
    const unclampedTop = offsetHours / ACTIVE_HOURS * 100;
    const topPercent = Math.min(unclampedTop, Math.max(100 - heightPercent, 0));

    return {
        top: topPercent,
        bottom: topPercent + heightPercent,
        height: heightPercent,
    };
}

function layoutEventsForDay(dayEvents, container) {
    const sortedEvents = dayEvents
        .map(event => ({
            event: event,
            bounds: getVisualEventBounds(event, container)
        }))
        .sort((a, b) => a.bounds.top - b.bounds.top || getEventEndTime(a.event) - getEventEndTime(b.event));

    let groups = [];
    let currentGroup = [];
    let currentGroupBottom = -1;

    sortedEvents.forEach(eventData => {
        if (currentGroup.length === 0 || eventData.bounds.top < currentGroupBottom) {
            currentGroup.push(eventData);
            currentGroupBottom = Math.max(currentGroupBottom, eventData.bounds.bottom);
        } else {
            groups.push(currentGroup);
            currentGroup = [eventData];
            currentGroupBottom = eventData.bounds.bottom;
        }
    });

    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }

    return groups.flatMap(group => {
        const lanes = [];
        return group.map(eventData => {
            let laneIndex = lanes.findIndex(laneBottom => eventData.bounds.top >= laneBottom);
            if (laneIndex === -1) {
                laneIndex = lanes.length;
                lanes.push(eventData.bounds.bottom);
            } else {
                lanes[laneIndex] = eventData.bounds.bottom;
            }

            return {
                event: eventData.event,
                bounds: eventData.bounds,
                laneIndex: laneIndex,
                laneCount: lanes.length,
            };
        }).map(positionedEvent => ({
            ...positionedEvent,
            laneCount: lanes.length,
        }));
    });
}

function renderCalendarSelector(readableCalendars) {
    calendars = readableCalendars;
    const list = getCalendarSelectorList();
    if (!list) {
        return;
    }

    const selectedCalendarIds = readStoredSelectedCalendarIds(readableCalendars);
    writeSelectedCalendarIds(selectedCalendarIds);
    list.innerHTML = "";

    readableCalendars.forEach(calendar => {
        const label = document.createElement("label");
        label.classList.add("gcal-calendar-selector__item");

        const input = document.createElement("input");
        input.type = "checkbox";
        input.name = "gcal_calendar";
        input.value = calendar.id;
        input.checked = selectedCalendarIds.includes(calendar.id);
        input.addEventListener("change", async () => {
            writeSelectedCalendarIds(getSelectedCalendarIds());
            clearCalendarEventsView();
            initializeDayColumns();
            await fetchCalendarEvents();
        });

        const swatch = document.createElement("span");
        swatch.classList.add("gcal-calendar-selector__swatch");
        swatch.style.backgroundColor = calendar.backgroundColor || "#cccccc";

        const name = document.createElement("span");
        name.classList.add("gcal-calendar-selector__name");
        name.textContent = calendar.summaryOverride || calendar.summary || calendar.id;

        label.appendChild(input);
        label.appendChild(swatch);
        label.appendChild(name);
        list.appendChild(label);
    });

    showCalendarSelector();
    setCalendarSelectorStatus(readableCalendars.length === 0 ? "No readable calendars found" : "");
}

function loadGoogleIdentityServices() {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

async function fetchGoogleCalendarJson(url, headers) {
    const response = await fetch(url, {headers: headers});
    if (!response.ok) {
        throw new Error(`Google Calendar request failed with status ${response.status}`);
    }
    return await response.json();
}

export async function applyGoogleCalendar() {
    const gCalCheckBox = document.querySelector("#gcal_sync_form input");
    updateCheckboxSettingState(gCalCheckBox);
    clearCalendarEventsView();

    if (gCalCheckBox.checked) {
        await initGoogleCalendar();
        initializeDayColumns();
        maybeShowGCalHelp();
    } else {
        hideCalendarSelector();
    }
}


async function initGoogleCalendar() {
    if (tokenClient !== null) {
        clarity("set", "gcal", "enable");
        tokenClient.requestAccessToken();
    } else {
        await loadGoogleIdentityServices(); // Ensure the Google Identity Services script is loaded
        tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: '40669866937-ihc4oc3n7mgb93dqo7ten8sv833uv6q4.apps.googleusercontent.com',
            scope: 'https://www.googleapis.com/auth/calendar.readonly',
            callback: async (resp) => {
                if (resp.error !== undefined) {
                    throw (resp);
                }
                accessToken = resp.access_token;
                await fetchCalendarEvents();
            },
            prompt: ''
        });
        tokenClient.requestAccessToken();
    }
}

async function fetchCalendarEvents() {
    clarity("set", "gcal", "complete");
    const headers = new Headers({
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
    });
    console.log("Fetching google events");
    showCalendarSelector();
    setCalendarSelectorStatus("Loading calendars...");

    // Calculate the date 5 days from now
    let fiveDaysFromNow = new Date();
    fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);

    // Calculate the end of the current month
    let endOfMonth = new Date();
    endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0); // Set to the last day of the current month

    // Use the latter of the two dates as timeMax
    let timeMin = new Date();
    timeMin.setHours(0, 0, 0, 0);
    let timeMax = fiveDaysFromNow > endOfMonth ? fiveDaysFromNow : endOfMonth;
    console.log("Fetching calendar colors");
    let colorResponse;
    let calendarsResponse;
    try {
        colorResponse = await fetchGoogleCalendarJson("https://www.googleapis.com/calendar/v3/colors", headers);
        const calendarParams = new URLSearchParams({
            'minAccessRole': 'reader',
            'showDeleted': false,
            'showHidden': false,
        }).toString();
        calendarsResponse = await fetchGoogleCalendarJson(`https://www.googleapis.com/calendar/v3/users/me/calendarList?${calendarParams}`, headers);
    } catch (error) {
        console.error("Failed to load Google calendars", error);
        setCalendarSelectorStatus("Unable to load calendars");
        return;
    }
    eventColors = colorResponse.event || {};

    const readableCalendars = calendarsResponse.items || [];
    renderCalendarSelector(readableCalendars);
    console.log("Calendars: ", readableCalendars);

    const params = new URLSearchParams({
        'timeMin': timeMin.toISOString(),
        'timeMax': timeMax.toISOString(),
        'showDeleted': false,
        'singleEvents': true,
        'maxResults': 100,
        'orderBy': 'startTime'
    }).toString();

    const selectedCalendarIds = getSelectedCalendarIds();
    if (readableCalendars.length === 0) {
        setCalendarSelectorStatus("No readable calendars found");
    } else {
        setCalendarSelectorStatus(selectedCalendarIds.length === 0 ? "No calendars selected" : "");
    }
    const eventResponses = await Promise.all(selectedCalendarIds.map(async calendarId => {
        try {
            const encodedCalendarId = encodeURIComponent(calendarId);
            const response = await fetchGoogleCalendarJson(`https://www.googleapis.com/calendar/v3/calendars/${encodedCalendarId}/events?${params}`, headers);
            return (response.items || []).map(event => ({
                ...event,
                calendarId: calendarId,
                calendarColor: getCalendarColor(calendarId)
            }));
        } catch (error) {
            console.error(`Failed to fetch events for Google Calendar ${calendarId}`, error);
            return [];
        }
    }));
    const events = eventResponses.flat().sort((a, b) => getEventStartTime(a) - getEventStartTime(b));
    document.querySelectorAll(`#calendar .event-dots`).forEach(p => {
        p.innerHTML = "";
    });

    events.forEach(event => {
        // Extracting start and end dates and times
        const startDate = event.start.date || event.start.dateTime;
        const endDate = event.end.date || event.end.dateTime;

        // Convert dates to more manageable formats
        const startDay = new Date(startDate).getDate();
        const endDay = new Date(endDate).getDate();

        // ignore day events
        if (!event.start.dateTime || !event.end.dateTime) {
            return;
        }

        // Extract times if they exist
        const startTime = new Date(event.start.dateTime).toTimeString().slice(0, 5);
        const endTime = new Date(event.end.dateTime).toTimeString().slice(0, 5);

        const eventColor = getEventColor(event);

        // Call addEventToDay for each day the event spans
        for (let day = startDay; day <= endDay; day++) {
            addEventToDay(day, startTime, endTime, eventColor);
        }
    });

    // Function to create an event element with dynamic height and top offset
    function createEventElement(event, eventColor, bounds, laneIndex, laneCount) {
        let eventElement = document.createElement("div");
        eventElement.classList.add("calendar__event");
        let options = { hour: '2-digit', minute: '2-digit' };
        let eventTimeElement = document.createElement("div");
        eventTimeElement.innerText = `${new Date(event.start.dateTime).toLocaleTimeString([], options)} – ${new Date(event.end.dateTime).toLocaleTimeString([], options)}`
        eventTimeElement.classList.add("calendar__event-time");
        let eventTitleElement = document.createElement("div");
        eventTitleElement.innerText = event.summary;
        eventTitleElement.classList.add("calendar__event-title");
        eventElement.appendChild(eventTimeElement);
        eventElement.appendChild(eventTitleElement);
        eventElement.style.backgroundColor = eventColor;

        const eventGapPercent = 0.5;
        const widthPercent = 100 / laneCount;
        eventElement.style.left = `calc(${laneIndex * widthPercent}% + ${eventGapPercent / 2}%)`;
        eventElement.style.right = "auto";
        eventElement.style.width = `calc(${widthPercent}% - ${eventGapPercent}%)`;
        eventElement.style.height = `${bounds.height}%`;
        eventElement.style.top = `${bounds.top}%`;

        return eventElement;
    }

    let today = new Date();
    let tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    let dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

    function appendEventsToColumn(dayEvents, columnElement) {
        layoutEventsForDay(dayEvents, columnElement).forEach(positionedEvent => {
            const eventColor = getEventColor(positionedEvent.event);
            const eventElement = createEventElement(
                positionedEvent.event,
                eventColor,
                positionedEvent.bounds,
                positionedEvent.laneIndex,
                positionedEvent.laneCount
            );
            columnElement.appendChild(eventElement);
        });
    }

    const todayEvents = [];
    const tomorrowEvents = [];
    const dayAfterTomorrowEvents = [];

    events.forEach(event => {
        const eventStart = new Date(event.start.dateTime || event.start.date);
        if (!event.start.dateTime || !event.end.dateTime) {
            return;
        }

        if (eventStart.toDateString() === today.toDateString()) {
            todayEvents.push(event);
        } else if (eventStart.toDateString() === tomorrow.toDateString()) {
            tomorrowEvents.push(event);
        } else if (eventStart.toDateString() === dayAfterTomorrow.toDateString()) {
            dayAfterTomorrowEvents.push(event);
        }
    });

    const todayColumn = document.getElementById("row_start");
    appendEventsToColumn(todayEvents, todayColumn);
    appendEventsToColumn(tomorrowEvents, todayColumn.nextElementSibling);
    appendEventsToColumn(dayAfterTomorrowEvents, todayColumn.nextElementSibling.nextElementSibling);
}

function addEventToDay(day, startTime, endTime, color) {
    // Determine the time of day for the event and draw a dot accordingly
    const dotElem = document.querySelector(`#calendar div[data-day="${day}"] .event-dots`);
    if (dotElem.childElementCount === 0) {
        const numberOfDots = 3;
        for (let i = 0; i < numberOfDots; i++) {
            let dot = document.createElement("span");
            dot.className = "dot";
            dotElem.appendChild(dot);
        }
    }
    const startHour = Number(startTime.split(':')[0]);
    let positionIndex;

    if (startHour < 11) {
        // Morning
        positionIndex = 0;
    } else if (startHour >= 11 && startHour < 15) {
        // Midday
        positionIndex = 1;
    } else {
        // Evening
        positionIndex = 2;
    }
    const dots = dotElem.children;
    dots[positionIndex].style.backgroundColor = color;
    dots[positionIndex].style.opacity = 1;
}
